/**
 * Central permission utility for APFv2.
 * All role/permission checks in the frontend should use these helpers.
 * DO NOT use `user.roleId === 'rN'` for access control — use these instead.
 */

/**
 * Returns true if the user has the given permission string.
 * Super Admin (r1) always returns true.
 *
 * @param {object|null} user - The current user object from useAuth()
 * @param {string} permission - A permission string e.g. 'bundles:manage'
 * @returns {boolean}
 */
export const hasPermission = (user, permission) => {
  if (!user) return false;
  if (user.roleId === 'r1') return true; // Super Admin bypass
  return Array.isArray(user.permissions) && user.permissions.includes(permission);
};

/**
 * Returns true if the user's role is scoped to a Tenant (tenant portal user).
 * Uses roleScope claim from JWT — works for built-in roles (r4, r7) AND custom
 * tenant roles (rN with scope='Tenant') created by Tenant Admins.
 *
 * Falls back to hardcoded r4/r7 check for sessions created before this migration
 * (old JWTs without the roleScope claim).
 *
 * @param {object|null} user - The current user object
 * @returns {boolean}
 */
// Known platform-only role IDs: r1=SuperAdmin, r2=PlatformAdmin, r3=PartnerAdmin,
// r5=SupportAdmin, r8/r9/r10=Support Agents (Note: r6=AppCreator is often tenant-bound)
const PLATFORM_ROLE_IDS = new Set(['r1', 'r2', 'r3', 'r5', 'r8', 'r9', 'r10']);

export const isTenantScoped = (user) => {
  if (!user) return false;
  // Primary: hardcoded built-in tenant role IDs (r4=TenantAdmin, r7=TenantUser)
  // These should ALWAYS be tenant-scoped regardless of what the JWT claim says.
  if (user.roleId === 'r4' || user.roleId === 'r7') return true;
  
  // Secondary: use roleScope claim (set at login, zero DB overhead)
  if (user.roleScope && user.roleScope !== 'Platform') return user.roleScope === 'Tenant';
  
  // Fallback: if the user has a tenantId AND is NOT a known platform-only role,
  // treat them as tenant-scoped. This covers custom tenant roles AND stale JWTs
  // that defaulted to roleScope='Platform' even though the user belongs to a tenant.
  if (user.tenantId && !PLATFORM_ROLE_IDS.has(user.roleId)) return true;
  return false;
};

/**
 * Returns true if the user is a Platform-level user (r1, r2, r3, r6, r5).
 * Inverse of isTenantScoped.
 *
 * @param {object|null} user - The current user object
 * @returns {boolean}
 */
export const isPlatformScoped = (user) => {
  return !isTenantScoped(user);
};
